import { adIntelModule } from '@/rules/page-access'
import { maAuthenticationGuard, maAuthorizationGuard, maCountryGuard, maParamsQueryNormalizeGuard } from '@/router/guards'
import { Param, Query, RouteName, TitleFont } from '@/base/constants/route'
import { PRODUCT_FAMILY, PRODUCTS } from '@/base/constants/authorization'

const adIntelRoutes = {
  path: 'ad-intelligence',
  name: RouteName.AD_INTELLIGENCE,
  meta: {
    productFamily: PRODUCT_FAMILY.AD_INTEL,
    shortTitle: 'AD',
    icon: 'tag',
    parent: true,
    requiredProducts: [PRODUCTS.AD_INTEL_ENTERPRISE],
  },
  redirect: { name: RouteName.AD_INTELLIGENCE_ANALYSIS },
  component: () => import('@/layouts/Dashboard.vue'),
  beforeEnter: maAuthenticationGuard,
  children: [
    {
      path: 'create',
      name: RouteName.AD_INTELLIGENCE_CREATE,
      meta: {
        title: 'Create',
        ignoreFromMenu: true,
        analyticsPageName: 'Ad Intel - Create',
        productFamily: PRODUCT_FAMILY.AD_INTEL,
        redirectRouteName: RouteName.AD_INTELLIGENCE_ANALYSIS_CREATIVE_ANALYSIS,
        requiredAccess: adIntelModule.createAccessRule,
      },
      alias: ['/adintelligence/create'],
      component: () => import('@/pages/ad-intelligence/CreateAds.vue'),
      beforeEnter: maAuthorizationGuard,
    },
    {
      path: 'manage',
      name: RouteName.AD_INTELLIGENCE_MANAGE,
      meta: {
        title: 'Manage & Run Ads',
        ignoreFromMenu: true,
        analyticsPageName: 'Ad Intel - Manage',
        productFamily: PRODUCT_FAMILY.AD_INTEL,
        redirectRouteName: RouteName.AD_INTELLIGENCE_ANALYSIS_CREATIVE_ANALYSIS,
        requiredAccess: adIntelModule.manageAccessRule,
      },
      alias: ['/adintelligence/manage'],
      component: () => import('@/pages/ad-intelligence/ManageAds.vue'),
      beforeEnter: maAuthorizationGuard,
    },
    {
      path: 'analysis',
      name: RouteName.AD_INTELLIGENCE_ANALYSIS,
      meta: {
        title: 'ANALYSIS',
        titleFont: TitleFont.HEADLINE,
      },
      redirect: { name: RouteName.AD_INTELLIGENCE_ANALYSIS_CREATIVE_ANALYSIS },
      children: [
        {
          path: 'creative-analysis/:trackId?',
          name: RouteName.AD_INTELLIGENCE_ANALYSIS_CREATIVE_ANALYSIS,
          meta: {
            title: 'Creative Analysis',
            analyticsPageName: 'Ad Intel - Creative Analysis',
            productFamily: PRODUCT_FAMILY.AD_INTEL,
            requiredParams: [Param.TRACK_ID],
            requiredQueries: [Query.ACTIVE_APP],
            requiredAccess: adIntelModule.creativeAnalysisAccessRule,
          },
          alias: ['/adintelligence/creative-analysis'],
          component: () => import('@/pages/ad-intelligence/CreativeAnalysis.vue'),
          beforeEnter: [
            maAuthorizationGuard,
            (to, from, next) => {
              if (!to.params.trackId && to.query.trackId) {
                to.params.trackId = to.query.trackId
                return next(to)
              }
              next()
            },
            maParamsQueryNormalizeGuard,
            maCountryGuard,
          ],
        },
        {
          path: 'advertiser-analysis/:trackId?/:listOfAdPublishers?',
          name: RouteName.AD_INTELLIGENCE_ANALYSIS_ADVERTISER_ANALYSIS,
          meta: {
            title: 'Advertiser Analysis',
            analyticsPageName: 'Ad Intel - Advertiser Analysis',
            productFamily: PRODUCT_FAMILY.AD_INTEL,
            requiredParams: [Param.TRACK_ID],
            requiredQueries: [Query.ACTIVE_APP],
            requiredAccess: adIntelModule.advertiserAnalysisAccessRule,
          },
          alias: ['/adintelligence/advertiser-analysis/:listOfAdPublishers?'],
          component: () => import('@/pages/ad-intelligence/AdvertiserAnalysis.vue'),
          beforeEnter: [maAuthorizationGuard, maParamsQueryNormalizeGuard, maCountryGuard],
        },
        {
          path: 'ad-publishers-analysis/:trackId?/:listOfAdvertisers?',
          name: RouteName.AD_INTELLIGENCE_ANALYSIS_AD_PUBLISHER_ANALYSIS,
          meta: {
            title: 'Ad Publishers Analysis',
            analyticsPageName: 'Ad Intel - Ad Publishers Analysis',
            productFamily: PRODUCT_FAMILY.AD_INTEL,
            requiredParams: [Param.TRACK_ID],
            requiredQueries: [Query.ACTIVE_APP],
            requiredAccess: adIntelModule.adPublishersAnalysisAccessRule,
          },
          alias: ['/adintelligence/ad-publisher-analysis/:listOfAdvertisers?'],
          component: () => import('@/pages/ad-intelligence/AdPublisherAnalysis.vue'),
          beforeEnter: [
            maAuthorizationGuard,
            (to, from, next) => {
              if (!to.params.trackId && to.query.trackId) {
                to.params.trackId = to.query.trackId
                return next(to)
              }
              next()
            },
            maParamsQueryNormalizeGuard,
            maCountryGuard,
          ],
        },
        {
          path: 'developer-analysis/:developerName?',
          name: RouteName.AD_INTELLIGENCE_ANALYSIS_DEVELOPER_ANALYSIS,
          meta: {
            title: 'Developer Analysis',
            analyticsPageName: 'Ad Intel - Developer Analysis',
            productFamily: PRODUCT_FAMILY.AD_INTEL,
            requiredParams: [Param.DEVELOPER_NAME],
            redirectRouteName: RouteName.AD_INTELLIGENCE_ANALYSIS_CREATIVE_ANALYSIS,
            requiredAccess: adIntelModule.developerAnalysisAccessRule,
          },
          alias: ['/adintelligence/developer-analysis'],
          component: () => import('@/pages/ad-intelligence/DeveloperAnalysis.vue'),
          beforeEnter: [
            maAuthorizationGuard,
            (to, from, next) => {
              if (!to.params.developerName && to.query.developerName) {
                to.params.developerName = to.query.developerName
                return next(to)
              }
              next()
            },
            maParamsQueryNormalizeGuard,
          ],
        },
        {
          path: 'media-analysis/:mediaId',
          name: RouteName.AD_INTELLIGENCE_ANALYSIS_MEDIA_ANALYSIS,
          meta: {
            title: 'Media Analysis',
            ignoreFromMenu: true,
            analyticsPageName: 'Ad Intel - Media Analysis',
            productFamily: PRODUCT_FAMILY.AD_INTEL,
            redirectRouteName: RouteName.AD_INTELLIGENCE_ANALYSIS_CREATIVE_ANALYSIS,
            requiredAccess: adIntelModule.mediaAnalysisAccessRule,
          },
          alias: ['/adintelligence/media-analysis/:mediaId'],
          component: () => import('@/pages/ad-intelligence/Creative.vue'),
          beforeEnter: maAuthorizationGuard,
        },
        {
          path: 'publisher-analysis',
          name: RouteName.AD_INTELLIGENCE_ANALYSIS_PUBLISHER_ANALYSIS,
          meta: {
            title: 'Publisher Analysis',
            ignoreFromMenu: true,
            analyticsPageName: 'Ad Intel - Publisher Analysis',
            productFamily: PRODUCT_FAMILY.AD_INTEL,
            redirectRouteName: RouteName.AD_INTELLIGENCE_ANALYSIS_CREATIVE_ANALYSIS,
            requiredAccess: adIntelModule.publisherAnalysisAccessRule,
          },
          alias: ['/adintelligence/publisher-analysis'],
          component: () => import('@/pages/ad-intelligence/PublisherAnalysis.vue'),
          beforeEnter: maAuthorizationGuard,
        },
        {
          path: 'list-of-advertisers',
          name: RouteName.AD_INTELLIGENCE_ANALYSIS_LIST_OF_ADVERTISERS_ANALYSIS,
          meta: {
            title: 'List Of Advertisers Analysis',
            ignoreFromMenu: true,
            analyticsPageName: 'Ad Intel - List Of Advertisers Analysis',
            productFamily: PRODUCT_FAMILY.AD_INTEL,
            redirectRouteName: RouteName.AD_INTELLIGENCE_ANALYSIS_CREATIVE_ANALYSIS,
            requiredAccess: adIntelModule.listOfAdvertisersAnalysisAccessRule,
          },
          alias: ['/adintelligence/list-of-advertisers'],
          component: () => import('@/pages/ad-intelligence/ListOfAdvertisers.vue'),
          beforeEnter: maAuthorizationGuard,
        },
        {
          path: 'list-of-ad-publishers',
          name: RouteName.AD_INTELLIGENCE_ANALYSIS_LIST_OF_AD_PUBLISHERS_ANALYSIS,
          meta: {
            title: 'List Of Publishers Analysis',
            ignoreFromMenu: true,
            analyticsPageName: 'Ad Intel - List Of Publisher Analysis',
            productFamily: PRODUCT_FAMILY.AD_INTEL,
            redirectRouteName: RouteName.AD_INTELLIGENCE_ANALYSIS_CREATIVE_ANALYSIS,
            requiredAccess: adIntelModule.listOfAdPublishersAnalysisAccessRule,
          },
          alias: ['/adintelligence/list-of-publishers'],
          component: () => import('@/pages/ad-intelligence/ListOfPublishers.vue'),
          beforeEnter: maAuthorizationGuard,
        },
        {
          path: 'analysis-ad-networks',
          name: RouteName.AD_INTELLIGENCE_ANALYSIS_AD_NETWORKS_ANALYSIS,
          meta: {
            title: 'Ad Networks Analysis',
            ignoreFromMenu: true,
            analyticsPageName: 'Ad Intel - Ad Network Analysis',
            productFamily: PRODUCT_FAMILY.AD_INTEL,
            redirectRouteName: RouteName.AD_INTELLIGENCE_ANALYSIS_CREATIVE_ANALYSIS,
            requiredAccess: adIntelModule.adNetworksAnalysisAccessRule,
          },
          alias: ['/adintelligence/analysis-ad-networks'],
          component: () => import('@/pages/ad-intelligence/AnalysisAdNetworks.vue'),
          beforeEnter: maAuthorizationGuard,
        },
      ],
    },
    {
      path: 'top-charts',
      name: RouteName.AD_INTELLIGENCE_TOP_CHARTS,
      meta: {
        title: 'TOP CHARTS',
        titleFont: TitleFont.HEADLINE,
      },
      redirect: { name: RouteName.AD_INTELLIGENCE_TOP_CHARTS_TOP_ADVERTISERS },
      children: [
        {
          path: 'top-advertisers',
          name: RouteName.AD_INTELLIGENCE_TOP_CHARTS_TOP_ADVERTISERS,
          meta: {
            title: 'Top Advertisers',
            analyticsPageName: 'Ad Intel - Top Advertisers',
            productFamily: PRODUCT_FAMILY.AD_INTEL,
            redirectRouteName: RouteName.AD_INTELLIGENCE_ANALYSIS_CREATIVE_ANALYSIS,
            requiredAccess: adIntelModule.topAdvertisersAccessRule,
          },
          alias: ['/adintelligence/top-advertisers'],
          component: () => import('@/pages/ad-intelligence/TopAdvertisers.vue'),
          beforeEnter: maAuthorizationGuard,
        },
        {
          path: 'top-ad-publishers',
          name: RouteName.AD_INTELLIGENCE_TOP_CHARTS_TOP_AD_PUBLISHERS,
          meta: {
            title: 'Top Ad Publishers',
            analyticsPageName: 'Ad Intel - Top Ad Publishers',
            productFamily: PRODUCT_FAMILY.AD_INTEL,
            redirectRouteName: RouteName.AD_INTELLIGENCE_ANALYSIS_CREATIVE_ANALYSIS,
            requiredAccess: adIntelModule.topAdPublishersAccessRule,
          },
          alias: ['/adintelligence/top-ad-publishers'],
          component: () => import('@/pages/ad-intelligence/TopAdPublishers.vue'),
          beforeEnter: maAuthorizationGuard,
        },
        {
          path: 'top-creatives',
          name: RouteName.AD_INTELLIGENCE_TOP_CHARTS_TOP_CREATIVES,
          meta: {
            title: 'Top Creatives',
            analyticsPageName: 'Ad Intel - Top Creatives',
            productFamily: PRODUCT_FAMILY.AD_INTEL,
            redirectRouteName: RouteName.AD_INTELLIGENCE_ANALYSIS_CREATIVE_ANALYSIS,
            requiredAccess: adIntelModule.topCreativesAccessRule,
          },
          alias: ['/adintelligence/top-creatives'],
          component: () => import('@/pages/ad-intelligence/TopCreatives.vue'),
          beforeEnter: maAuthorizationGuard,
        },
        {
          path: 'top-developers',
          name: RouteName.AD_INTELLIGENCE_TOP_CHARTS_TOP_DEVELOPERS,
          meta: {
            title: 'Top Developers',
            analyticsPageName: 'Ad Intel - Top Developers',
            productFamily: PRODUCT_FAMILY.AD_INTEL,
            redirectRouteName: RouteName.AD_INTELLIGENCE_ANALYSIS_CREATIVE_ANALYSIS,
            requiredAccess: adIntelModule.topDevelopersAccessRule,
          },
          alias: ['/adintelligence/top-developers'],
          component: () => import('@/pages/ad-intelligence/TopDevelopers.vue'),
          beforeEnter: maAuthorizationGuard,
        },
      ],
    },
    {
      path: 'collection',
      name: RouteName.AD_INTELLIGENCE_COLLECTION,
      meta: {
        title: 'COLLECTION',
        titleFont: TitleFont.HEADLINE,
      },
      redirect: { name: RouteName.AD_INTELLIGENCE_COLLECTION_APP_COLLECTIONS },
      children: [
        {
          path: 'app-collections/:collectionId?/:detail?',
          name: RouteName.AD_INTELLIGENCE_COLLECTION_APP_COLLECTIONS,
          meta: {
            title: 'App Collections',
            analyticsPageName: 'Ad Intel - App Collections',
            productFamily: PRODUCT_FAMILY.AD_INTEL,
            redirectRouteName: RouteName.AD_INTELLIGENCE_ANALYSIS_CREATIVE_ANALYSIS,
            requiredAccess: adIntelModule.appCollectionsAccessRule,
          },
          alias: ['/adintelligence/app-collections/:collectionId?/:detail?'],
          component: () => import('@/pages/ad-intelligence/AppCollections.vue'),
          beforeEnter: maAuthorizationGuard,
        },
        {
          path: 'creative-collections/:collectionId?',
          name: RouteName.AD_INTELLIGENCE_COLLECTION_CREATIVE_COLLECTIONS,
          meta: {
            title: 'Creative Collections',
            analyticsPageName: 'Ad Intel - Creative Collections',
            productFamily: PRODUCT_FAMILY.AD_INTEL,
            redirectRouteName: RouteName.AD_INTELLIGENCE_ANALYSIS_CREATIVE_ANALYSIS,
            requiredAccess: adIntelModule.creativeCollectionsAccessRule,
          },
          alias: ['/adintelligence/creative-collections/:collectionId?'],
          component: () => import('@/pages/ad-intelligence/CreativeCollections.vue'),
          beforeEnter: maAuthorizationGuard,
        },
      ],
    },
  ],
}

export default adIntelRoutes
